import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentApi, settingsApi, type AdminSettingsPayload } from '../api/payment';
import { adminApi, type AdminUser } from '../api/admin';
import { useAuthContext } from '../contexts/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SkeletonList } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { CheckIcon } from '../components/ui/icons';
import { useToast } from '../components/ui/Toast';
import { formatDate, formatCurrency } from '../utils/format';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  cancelled: 'Cancelado',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600',
  cancelled: 'bg-gray-100 text-gray-600',
};

const PLAN_LABELS: Record<string, string> = {
  FREE: 'Gratuito',
  PREMIUM: 'Premium',
  COURTESY: 'Cortesia',
  LIFE_PARTNER: 'Life Partner',
};

const PLAN_COLORS: Record<string, string> = {
  FREE: 'bg-gray-100 text-gray-500',
  PREMIUM: 'bg-amber-100 text-amber-700',
  COURTESY: 'bg-blue-100 text-blue-700',
  LIFE_PARTNER: 'bg-purple-100 text-purple-700',
};

type Tab = 'users' | 'premiums' | 'payments' | 'settings';

const TABS: { id: Tab; label: string }[] = [
  { id: 'users', label: 'Usuários' },
  { id: 'premiums', label: 'Premiums' },
  { id: 'payments', label: 'Pagamentos' },
  { id: 'settings', label: 'Configurações' },
];

export function AdminScreen() {
  const { isAdmin } = useAuthContext();
  if (!isAdmin) return <Navigate to="/" />;
  return <AdminContent />;
}

function AdminContent() {
  const { user: currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('users');
  const [settingsForm, setSettingsForm] = useState<AdminSettingsPayload>({});

  const { data: payments, isLoading: loadingPayments } = useQuery({
    queryKey: ['admin', 'payments'],
    queryFn: paymentApi.listPayments,
    staleTime: 30_000,
  });

  const { data: users, isLoading: loadingUsers } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: adminApi.listUsers,
    staleTime: 30_000,
  });

  const { data: aboutData } = useQuery({
    queryKey: ['settings', 'about'],
    queryFn: settingsApi.getAbout,
    staleTime: 60_000,
  });

  const confirmPaymentMutation = useMutation({
    mutationFn: paymentApi.confirmPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'payments'] });
      addToast('Pagamento confirmado!', 'success');
    },
    onError: () => addToast('Erro ao confirmar pagamento.', 'error'),
  });

  const grantCourtesyMutation = useMutation({
    mutationFn: (userId: string) => adminApi.grantCourtesy(userId, 30),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      addToast('Cortesia concedida por 30 dias!', 'success');
    },
    onError: () => addToast('Erro ao conceder cortesia.', 'error'),
  });

  const grantLifePartnerMutation = useMutation({
    mutationFn: adminApi.grantLifePartner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      addToast('Life Partner ativado!', 'success');
    },
    onError: () => addToast('Erro ao ativar Life Partner.', 'error'),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: settingsApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'about'] });
      addToast('Configurações salvas!', 'success');
    },
    onError: () => addToast('Erro ao salvar configurações.', 'error'),
  });

  React.useEffect(() => {
    if (aboutData) {
      setSettingsForm({
        'app-name': aboutData['app-name'],
        'app-tagline': aboutData['app-tagline'],
        'about-description': aboutData['about-description'],
        'about-contact-email': aboutData['about-contact-email'],
        'about-whatsapp': aboutData['about-whatsapp'],
        'terms-url': aboutData['terms-url'] ?? '',
        'privacy-url': aboutData['privacy-url'] ?? '',
        'support-url': aboutData['support-url'] ?? '',
        'premium-price': aboutData['premium-price'] ?? '',
      });
    }
  }, [aboutData]);

  const handleSaveSettings = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettingsMutation.mutate(settingsForm);
  };

  const approvedPayments = payments?.filter((p) => p.status === 'approved') ?? [];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <PageHeader title="Painel Admin" showBack />

      <div className="flex border-b border-gray-200 bg-white overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={[
              'flex-shrink-0 px-4 py-3 text-sm font-semibold transition-colors touch-manipulation',
              activeTab === tab.id
                ? 'text-brand-600 border-b-2 border-brand-500'
                : 'text-gray-500 hover:text-gray-700',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 px-4 py-4 space-y-3 pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)] overflow-y-auto">

        {activeTab === 'users' && (
          loadingUsers ? (
            <SkeletonList count={5} />
          ) : !users?.length ? (
            <EmptyState title="Nenhum usuario cadastrado." />
          ) : (
            users.map((user: AdminUser) => {
              const isCurrentUser = user.cognitoUserId === currentUser?.userId;
              return (
              <div
                key={user.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    {user.planUntil && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Até {formatDate(user.planUntil)}
                      </p>
                    )}
                  </div>
                  {isCurrentUser ? (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 bg-red-100 text-red-700">
                      Admin
                    </span>
                  ) : (
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${
                      PLAN_COLORS[user.plan] ?? 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {PLAN_LABELS[user.plan] ?? user.plan}
                  </span>
                  )}
                </div>
                {!isCurrentUser && (
                <div className="flex gap-2">
                  <button
                    onClick={() => grantCourtesyMutation.mutate(user.id)}
                    disabled={grantCourtesyMutation.isPending}
                    className="flex-1 py-2 text-xs font-semibold text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition-colors touch-manipulation disabled:opacity-50"
                  >
                    Cortesia 30d
                  </button>
                  <button
                    onClick={() => grantLifePartnerMutation.mutate(user.id)}
                    disabled={grantLifePartnerMutation.isPending}
                    className="flex-1 py-2 text-xs font-semibold text-purple-600 border border-purple-200 rounded-xl hover:bg-purple-50 transition-colors touch-manipulation disabled:opacity-50"
                  >
                    Life Partner
                  </button>
                </div>
                )}
              </div>
              );
            })
          )
        )}

        {activeTab === 'premiums' && (
          loadingPayments ? (
            <SkeletonList count={4} />
          ) : !approvedPayments.length ? (
            <EmptyState title="Nenhum pagamento aprovado ainda." />
          ) : (
            approvedPayments.map((payment) => (
              <div
                key={payment.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {payment.memberName ?? payment.cognitoUserId}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Ativado em {formatDate(payment.confirmedAt ?? payment.createdAt)}
                      {payment.memberPlanUntil && (
                        <> - até {formatDate(payment.memberPlanUntil)}</>
                      )}
                    </p>
                  </div>
                  <p className="text-base font-bold text-gray-900 flex-shrink-0">
                    {formatCurrency(payment.amount)}
                  </p>
                </div>
                <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  Premium ativo
                </span>
              </div>
            ))
          )
        )}

        {activeTab === 'payments' && (
          loadingPayments ? (
            <SkeletonList count={5} />
          ) : !payments?.length ? (
            <EmptyState title="Nenhum pagamento ainda." />
          ) : (
            payments.map((payment) => (
              <div
                key={payment.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {payment.memberName ?? payment.cognitoUserId}
                      </p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                        payment.memberPlan === 'PREMIUM' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {payment.memberPlan === 'PREMIUM' ? 'Premium' : 'Free'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(payment.createdAt)}
                      {payment.memberPlan === 'PREMIUM' && payment.memberPlanUntil && (
                        <> - Premium até {formatDate(payment.memberPlanUntil)}</>
                      )}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${
                      STATUS_COLORS[payment.status] ?? 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {STATUS_LABELS[payment.status] ?? payment.status}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-base font-bold text-gray-900">
                    {formatCurrency(payment.amount)}
                  </p>
                  {payment.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => confirmPaymentMutation.mutate(payment.id)}
                      disabled={confirmPaymentMutation.isPending}
                      loading={confirmPaymentMutation.isPending}
                      title="Confirmar pagamento"
                      aria-label="Confirmar pagamento"
                    >
                      <CheckIcon size={16} />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )
        )}

        {activeTab === 'settings' && (
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
              <p className="font-bold text-gray-900">Identidade do App</p>
              <Input
                label="Nome do app"
                value={settingsForm['app-name'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'app-name': e.target.value }))}
                name="appName"
              />
              <Input
                label="Tagline"
                value={settingsForm['app-tagline'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'app-tagline': e.target.value }))}
                name="appTagline"
              />
              <Input
                label="Descrição (tela Sobre)"
                value={settingsForm['about-description'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'about-description': e.target.value }))}
                name="aboutDescription"
              />
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
              <p className="font-bold text-gray-900">Contato e Suporte</p>
              <Input
                label="E-mail de contato"
                type="email"
                value={settingsForm['about-contact-email'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'about-contact-email': e.target.value }))}
                name="contactEmail"
              />
              <Input
                label="WhatsApp"
                placeholder="+5511999999999"
                value={settingsForm['about-whatsapp'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'about-whatsapp': e.target.value }))}
                name="whatsapp"
              />
              <Input
                label="URL de Suporte"
                placeholder="https://..."
                value={settingsForm['support-url'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'support-url': e.target.value }))}
                name="supportUrl"
              />
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
              <p className="font-bold text-gray-900">Links Legais</p>
              <Input
                label="Termos de Uso (URL)"
                placeholder="https://..."
                value={settingsForm['terms-url'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'terms-url': e.target.value }))}
                name="termsUrl"
              />
              <Input
                label="Politica de Privacidade (URL)"
                placeholder="https://..."
                value={settingsForm['privacy-url'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'privacy-url': e.target.value }))}
                name="privacyUrl"
              />
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
              <p className="font-bold text-gray-900">Monetização</p>
              <Input
                label="Preço Premium (R$)"
                type="number"
                step="0.01"
                placeholder="9.90"
                value={settingsForm['premium-price'] ?? ''}
                onChange={(e) => setSettingsForm((f) => ({ ...f, 'premium-price': e.target.value }))}
                name="premiumPrice"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="md"
              loading={updateSettingsMutation.isPending}
              title="Salvar configurações"
              aria-label="Salvar configurações"
            >
              <CheckIcon size={16} />
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
